

<template>
  <div class="tinymce-editor">
    <div v-if="editorLoading" class="tinymce-loading-mask">
      <div class="tinymce-loading-text">Loading editor...</div>
    </div>
    <editor
      ref="editorRef"
      v-model="editorValue"
      :init="init"
      :disabled="disabled"
      :api-key="tinymceApiKey"
      :cloud-channel="tinymceCloudChannel"
      @input="onInput"
    />
  </div>
</template>
<script>
import Editor from "@tinymce/tinymce-vue";
// doc cn http://tinymce.ax-z.cn/
// doc en https://www.tiny.cloud/docs/configure/
import {CancelToken, isCancel} from "axios"
import {getVideoPosterImageFileByFile} from "../utils/video";

export default {
  name: "TinymceEditor",
  components: {
    Editor,
  },
  model: {
    prop: "value",
    event: "input",
  },
  props: {
    value: String,
    height: {
      type: [String, Number],
      default: 500,
    },
    baseUrl: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    menubar: {
      type: Boolean,
      default: false,
    },
    plugins: {
      type: [String, Array],
      default:
        "code save searchreplace indent2em lineheight table fullscreen autolink link lists media charmap wordcount help",
    },
    toolbar: {
      type: [String, Array],
      default:
        "undo redo | bold italic underline forecolor backcolor removeformat | formatselect | fontfamily fontsize | alignleft aligncenter alignright alignjustify ltr rtl indent2em lineheight | bullist numlist outdent indent | table | link lists media charmap | searchreplace",
    },
  },
  data() {
    return {
      tinymceApiKey: process.env.VUE_APP_TINYMCE_API_KEY || "no-api-key",
      tinymceCloudChannel: "7",
      editorLoading: true,
      maximumSize: 100,
      videoUploadCancel: null,
      init: {
        selector: "textarea",
        relative_urls: false,
        remove_script_host: false,
        skin_url: "/static/js/skins/ui/oxide",
        height: this.height,
        min_height: 200,
        max_height: 500,
        toolbar_mode: "wrap",
        menubar: this.menubar,
        plugins: this.plugins,
        toolbar: this.toolbar,
        custom_colors: true,
        color_cols: 8,
        convert_fonts_to_spans: true,
        content_style: "p {margin: 5px 0;}",
        font_size_formats: "8pt 10pt 12pt 14pt 18pt 24pt 36pt",
        save_enablewhendirty: false, //默认情况下，内容区有变化才激活上面的保存按钮。可用参数save_enablewhendirty:false关闭该功能。
        font_family_formats:
          "Arial=arial,helvetica,sans-serif; Courier New=courier new,courier,monospace; AkrutiKndPadmini=Akpdmi-n;宋体=宋体;黑体=黑体;仿宋=仿宋;微软雅黑=微软雅黑;楷体-GB2312=楷体-GB2312",
        fullpage_default_font_family: "微软雅黑",
        default_link_target: "_blank",
        link_assume_external_targets: true,
        branding: false,
        //此处为图片上传处理函数，这个直接用了base64的图片形式上传图片， //如需ajax上传可参考https://www.tiny.cloud/docs/configure/file-image-upload/#images_upload_handler
        images_upload_handler: this.imageUploadHandler,
        images_title: true,
        save_onsavecallback: this.onsavecallback,
        init_instance_callback: (editor) => {
          const tinymceGlobal = editor.editorManager;
          const version = tinymceGlobal
            ? `${tinymceGlobal.majorVersion}.${tinymceGlobal.minorVersion}`
            : "unknown";
          console.info(
            `[TinyMCE] cloud-channel=${this.tinymceCloudChannel}, loaded version=${version}`,
          );
          this.editorLoading = false;
          editor.on("ExecCommand", this.onExecCommand);
          // Color picker RGB/# fields live inside el-form; native HTML5
          // validation paints the extra red triangle Tiny cannot reproduce in Fiddle.
          const hostForm = (this.$el && this.$el.closest)
            ? this.$el.closest("form")
            : null;
          if (hostForm) {
            hostForm.setAttribute("novalidate", "novalidate");
          }
        },
        file_picker_types: "media",
        file_picker_callback: this.mediaFilePickerCallbackHandler,
        media_alt_source: false,
        media_poster: false,
        paste_data_images: false
      },
      editorValue: this.value,
    };
  },
  computed: {
    isPad() {
      return /(pad|ipad)/i.test(navigator.userAgent.toLowerCase());
    },
  },
  created() {
    if (!this.isPad) {
      this.init.toolbar = `${this.toolbar} fullscreen`;
    }
    this.getVideoConfig();
  },
  methods: {
    getVideoConfig() {
      this.axios.get("/sys/getVideoConfig").then(({code, data}) => {
        if (200 === code) {
          this.maximumSize = data.maximumSize;
        }
      });
    },
    onInput(value) {
      const editorInstance = this.getEditor();
      this.$emit("input", value, editorInstance);
    },
    getEditor() {
      return this.$refs && this.$refs.editorRef ? this.$refs.editorRef.editor : null;
    },
    onsavecallback(e) {
      this.$emit("onSave", e);
    },
    onExecCommand(e) {
      if (e.command === "mceUpdateImage") {
        this.$nextTick(() => {
          const {alt, width, height, src} = e.value;
          let figcaption = `<figcaption><a href="${src}" target="_blank" rel="noopener">查看原图</a></figcaption>`;
          let img = `<img src="${src}" alt="${alt}" width="${width}" height="${height}" />`;
          let figure = `<figure class="image align-left" style="text-align: center;">${img}${figcaption}</figure>`;
          let imgReg = `<img src="${src}".*?/>`;
          let reg = new RegExp(`${imgReg}\n`);
          if (!reg.test(this.editorValue)) {
            let valReg = new RegExp(imgReg);
            this.editorValue = this.editorValue.replace(valReg, figure);
          }
          if (reg.test(this.editorValue)) {
            let aReg = new RegExp(`${imgReg}\n<figcaption>.*?</figcaption>`);
            this.editorValue = this.editorValue.replace(
              aReg,
              `${img}\n${figcaption}`
            );
          }
        });
      }
    },
    imageUploadHandler(blobInfo, success, fail) {
      const file = blobInfo.blob();
      if (file.size > 2 * 1024 * 1024) {
        fail("Image file size exceeds server limit");
        return;
      }
      this.imageUploadRequest(file).then(({code, data}) => {
        if (code === 200) {
          success(data.rawUrl);
        } else {
          fail(code);
        }
      }).catch(() => {
        fail(500);
      });
    },
    imageUploadRequest(file) {
      let formData = new FormData();
      formData.append("file", file, file.name);
      return this.axios.post("/content/uploadImage", formData, {
        headers: {"Content-Type": "multipart/form-data"},
      });
    },
    videoTemplateCallback(data) {
      const {width, height, source, poster, sourcemime, altsource, altsourcemime} = data;
      return `<video width="${width}" height="${height}" ${poster ? `poster="${poster}"` : ""} controls="controls">\n<source src="${source}" ${sourcemime ? `type="${sourcemime}"` : ""}/>\n ${altsource ? `<source src="${altsource}" type="${altsourcemime}" />\n` : ""}</video>`
    },
    uploadPosterImage({url, file, progress}, cb) {
      progress.setTitle("Generating poster...");
      getVideoPosterImageFileByFile(file).then(posterFile => {
        return this.imageUploadRequest(posterFile);
      }).then(({code, data}) => {
        if (200 === code) {
          this.$message({
            message: "Upload video successfully !",
            type: "success"
          });
          progress.success();
          progress.close();
          cb(url, {
            title: file.name,
            poster: data.rawUrl
          });
        } else {
          this.$message({
            message: "Upload video failure !",
            type: "error"
          });
          progress.setTitle("Upload video failure !");
          progress.fail();
        }
      }).catch(() => {
        this.$message({
          message: "Upload video failure",
          type: "error"
        });
        progress.setTitle("Upload video failure !");
        progress.fail();
      });
    },
    uploadMedia({file, type, action}, cb) {
      const size = file.size / 1024 / 1024; // MB
      if (size > this.maximumSize) {
        this.$message({
          message: `Selected ${type} file size exceeds ${this.maximumSize}MB`,
          type: "warning"
        });
        return;
      }
      const progress = this.$uploadProgress({
        title: `Uploading ${type} file...`,
        onCancel: () => {
          if (typeof this.videoUploadCancel === "function") {
            this.videoUploadCancel();
          }
        }
      });
      let formData = new FormData();
      formData.append("file", file, file.name);
      this.axios.post(action, formData, {
        headers: {"Content-Type": "multipart/form-data"},
        cancelToken: new CancelToken(cancel => {
          this.videoUploadCancel = cancel;
        }),
        onUploadProgress: (e) => {
          if (e.lengthComputable) {
            const percentage = ((e.loaded / e.total) * 100) | 0;
            progress.setPercentage(percentage);
          }
        },
      }).then(({code, data}) => {
        if (200 === code) {
          if (type === "video") {
            this.uploadPosterImage({file, url: data.url, progress}, cb);
          } else {
            this.$message({
              message: `Upload ${type} successfully !`,
              type: "success"
            });
            progress.success();
            progress.close();
            cb(data.url, {
              title: file.name
            });
          }
        } else {
          this.$message({
            message: `Upload ${type} failure !`,
            type: "error"
          });
          progress.setTitle(`Upload ${type} failure !`);
          progress.fail();
        }
      }).catch((err) => {
        progress.setTitle(`Upload ${type} failure !`);
        progress.fail();
        if (isCancel(err)) {
          progress.setTitle("Cancel successfully !");
          this.videoUploadCancel = null;
          this.$message({
            message: "Cancel successfully !",
            type: "warning"
          });
        }
      });
    },
    mediaFilePickerCallbackHandler(cb, value, meta) {
      const _this = this;
      if (meta.filetype !== "media") {
        return
      }
      const accepts = ".mp4,.mp3";
      let input = document.createElement("input");
      input.setAttribute("type", "file");
      input.setAttribute("accept", accepts)
      input.onchange = function () {
        const file = this.files[0];
        const acceptsStr = accepts.replace(/\./g, "").replace(/,/g, "|");
        const acceptReg = new RegExp(`.(?:${acceptsStr})$`, "i");
        if (!acceptReg.test(file.name)) {
          _this.$message({
            message: "Only .mp3,.mp4 file can be uploaded",
            type: "warning"
          });
          return;
        }
        const type = /.mp3$/.test(file.name) ? "audio" : "video";
        const params = {file, type, action: `/content/upload/${type}`};
        _this.uploadMedia(params, cb);
      }
      input.click();
    },
  },
  watch: {
    value(value) {
      this.editorValue = value;
    },
  },
};
</script>
<style lang="scss" scoped>
$border-color: #dcdfe6;
.tinymce-editor {
  padding: 0 !important;
  margin-bottom: 1px;

  ::v-deep {
    .tox {
      .tox-toolbar__group {
        border: none !important;
        padding: 0 !important;
      }

      border-color: $border-color !important;

      &:not([dir="rtl"]) .tox-toolbar__group:not(:last-of-type) {
        border-right-color: $border-color !important;
      }

      button[title="Bold"] svg,
      button[title="Italic"] svg,
      button[title="Underline"] svg,
      div[title="Text color"] svg,
      div[title="Background color"] svg {
        fill: #222f3e !important;
      }

      button[title="Clear formatting"] svg {
        fill: #e6a23c !important;
      }

      button[title="Fullscreen"] svg {
        fill: #d52b1e !important;
      }

      .tox-tbtn {
        height: 26px !important;
      }

      .tox-tbtn svg {
        fill: #409eff !important;
      }

      .tox-tbtn:hover,
      .tox-tbtn--enabled,
      .tox-tbtn--enabled:hover {
        background: #ecf5ff !important;
        cursor: pointer;
      }

      .tox-tbtn--disabled,
      .tox-tbtn--disabled:hover,
      .tox-tbtn:disabled,
      .tox-tbtn:disabled:hover {
        background: none !important;
        cursor: default !important;
      }

      .tox-tbtn--disabled svg,
      .tox-tbtn--disabled:hover svg,
      .tox-tbtn:disabled svg,
      .tox-tbtn:disabled:hover svg {
        fill: #dcdfe6 !important;
      }

      .tox-toolbar,
      .tox-toolbar__overflow,
      .tox-toolbar__primary {
        background: url("data:image/svg+xml;charset=utf8,%3Csvg height='31px' viewBox='0 0 40 39px' width='40' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='0' y='29px' width='100' height='1' fill='%23dcdfe6'/%3E%3C/svg%3E") left 0 top 0 #fff !important;
      }

      .tox-statusbar {
        border-top-color: $border-color !important;
      }
    }
  }

  .tinymce-loading-mask {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.7);
    z-index: 10;
    font-size: 12px;
    color: #909399;
  }
}
</style>
<style lang="scss">
/* Color picker is portaled to body; hide native/Tiny invalid triangle next to R/G/B/# */
.tox .tox-control-wrap__status-icon-invalid,
.tox .tox-form__group .tox-control-wrap__status-icon-invalid,
.tox-color-picker-container .tox-icon.tox-control-wrap__status-icon-invalid {
  display: none !important;
}
.tox .tox-rgb-form input:invalid,
.tox .tox-textfield:invalid,
.tox input:invalid {
  box-shadow: none !important;
}
.tox input:-moz-ui-invalid {
  box-shadow: none !important;
}
.tox .tox-menu.tox-swatches-menu {
  z-index: 1300 !important;
}
</style>